import React from "react";
import { motion } from "framer-motion";
import {
  Building2,
  MapPin,
  Phone,
  Navigation,
  Globe,
  Star,
  Edit3,
  Trash2,
  Plus
} from "lucide-react";

const EmptyState = () => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="flex flex-col justify-center items-center p-8 text-center"
  >
     <div className="flex justify-center items-center mb-4 w-16 h-16 bg-gradient-to-r from-[#FF6F6E]/20 to-[#FFB5B4]/20 rounded-full">
       <Building2 className="w-8 h-8 text-[#FF6F6E]" />
     </div>
    <h3 className="mb-2 text-lg font-semibold text-gray-900">No Branches Yet</h3>
    <p className="text-gray-500">
      Start by adding your first branch location
    </p>
  </motion.div>
);

const BranchPreview = ({ branchData, onEdit, onRemove, canEdit, canDelete }) => {
  if (!branchData || !branchData.name) {
    return <EmptyState />;
  }

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="overflow-hidden relative p-6 bg-gradient-to-br from-white rounded-2xl border border-gray-100 shadow-sm transition-all duration-300 group to-blue-50/30 hover:shadow-lg"
    >
      {/* Background Pattern */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-50 to-transparent rounded-full opacity-50 translate-x-16 -translate-y-16" />
      
      {/* Header */}
      <div className="flex relative justify-between items-start mb-4">
        <div className="flex gap-3 items-center">
           <div className="p-3 text-white bg-gradient-to-r from-[#FF6F6E] to-[#FF9A99] rounded-xl shadow-lg">
             <Building2 className="w-6 h-6" />
           </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 transition-colors group-hover:text-[#FF6F6E]">
              {branchData.name}
            </h3>
            <div className="flex gap-1 items-center mt-1">
              <Star className="w-4 h-4 text-yellow-400 fill-current" />
              <span className="text-sm text-gray-500">Preview</span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 items-center">
          {canEdit && onEdit && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => onEdit(branchData)}
               className="p-2 text-gray-400 rounded-lg transition-colors hover:text-[#FF6F6E] hover:bg-[#FF6F6E]/10"
            >
              <Edit3 className="w-4 h-4" />
            </motion.button>
          )}
          {canDelete && onRemove && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => onRemove(branchData.id)}
              className="p-2 text-gray-400 rounded-lg transition-colors hover:text-red-600 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4" />
            </motion.button>
          )}
        </div>
      </div>

      {/* Location Info */}
      <div className="mb-4 space-y-3">
        {(branchData.region || branchData.location) && (
          <div className="flex gap-3 items-center p-3 bg-gray-50 rounded-xl">
             <div className="p-2 bg-[#FF6F6E]/10 rounded-lg">
               <MapPin className="w-4 h-4 text-[#FF6F6E]" />
             </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">
                {branchData.location || "Location not set"}
              </p>
              <p className="text-xs text-gray-500">
                {branchData.region || "Region not set"}
              </p>
            </div>
          </div>
        )}

        {branchData.phoneNumber && (
          <div className="flex gap-3 items-center p-3 bg-gray-50 rounded-xl">
             <div className="p-2 bg-[#FF9A99]/10 rounded-lg">
               <Phone className="w-4 h-4 text-[#FF9A99]" />
             </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">{branchData.phoneNumber}</p>
              <p className="text-xs text-gray-500">Contact Number</p>
            </div>
          </div>
        )}

        {branchData.googleMapsLink && (
          <div className="flex gap-3 items-center p-3 bg-gray-50 rounded-xl">
             <div className="p-2 bg-[#FF8584]/10 rounded-lg">
               <Navigation className="w-4 h-4 text-[#FF8584]" />
             </div>
            <div className="flex-1">
              <a 
                href={branchData.googleMapsLink} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm font-medium text-[#FF6F6E] hover:text-[#E55A59] transition-colors underline"
              >
                View on Google Maps
              </a>
              <p className="text-xs text-gray-500">Navigation Link</p>
            </div>
          </div>
        )}

        {/* Additional Details */}
        {(branchData.district || branchData.suburb || branchData.landmark) && (
          <div className="space-y-2">
            {branchData.district && (
              <div className="flex gap-2 items-center text-sm text-gray-600">
                <Navigation className="w-4 h-4 text-gray-400" />
                <span>District: {branchData.district}</span>
              </div>
            )}
            {branchData.suburb && (
              <div className="flex gap-2 items-center text-sm text-gray-600">
                <Globe className="w-4 h-4 text-gray-400" />
                <span>Suburb: {branchData.suburb}</span>
              </div>
            )}
            {branchData.landmark && (
              <div className="flex gap-2 items-center text-sm text-gray-600">
                <Star className="w-4 h-4 text-gray-400" />
                <span>Landmark: {branchData.landmark}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex justify-between items-center pt-4 border-t border-gray-100">
        <div className="flex gap-2 items-center text-xs text-gray-500">
          <Plus className="w-3 h-3" />
          <span>Preview Mode</span>
        </div>
         <div className="px-3 py-1 text-xs font-medium text-[#FF8584] bg-[#FF8584]/10 rounded-full">
           Draft
         </div>
      </div>

      {/* Hover Effect Overlay */}
      <div className="absolute inset-0 bg-gradient-to-r to-transparent opacity-0 transition-opacity duration-300 pointer-events-none from-[#FF6F6E]/5 group-hover:opacity-100" />
    </motion.div>
  );
};

export default BranchPreview;
